/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ActionIcon,
  Anchor,
  Image,
  Loader,
  NumberFormatter,
  NumberInput
} from '@mantine/core';
import { X } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import AppStack from '../../common/AppStack';
import ProductLineItemAddonsDisplay from '../products/ProductLineItemAddonsDisplay';
import WsCheckoutCartProductCardAlert from './sections/checkoutContent/WsCheckoutCartProductCardAlert';
import WsCheckoutCartProductCardBundleItems from './sections/checkoutContent/WsCheckoutCartProductCardBundleItems';

const CartDrawerLineItem = ({
  productUrl,
  onNavigate,
  lineItem,
  lineItemInfo,
  disabled,
  submitting,
  onRemove,
  onUpdate,
  onCustomize,
  onRemoveUnavailableProducts
}) => {
  const [debouncedQuantity, setDebouncedQuantity] = useState(null);
  const [quantity, setQuantity] = useState(null);
  const [removing, setRemoving] = useState(false);

  const {
    previewImage,
    quantityLeft,
    outOfStock,
    tooManyInCart,
    isUnavailable,
    hasChanges,
    requiresProductUpdate,
    requiresPrintImagesUpdate,
    latestVersionEcomStoreCartProduct,
    packageItemsWithReferences,
    ecomStoreCartProduct,
    ecomStoreCartPackage
  } = lineItemInfo || {};

  useEffect(() => {
    if (lineItem) {
      setQuantity(lineItem.quantity);
    }
  }, [lineItem]);

  useEffect(() => {
    if (debouncedQuantity !== null) {
      if (debouncedQuantity !== lineItem.quantity) {
        onUpdate(
          lineItem.isBundleProduct
            ? {
                ecomStoreCartPackages: [
                  {
                    pkEcomStoreCartPackage: lineItem.entityId,
                    count: debouncedQuantity
                  }
                ]
              }
            : {
                ecomStoreCartProducts: [
                  {
                    pkEcomStoreCartProduct: lineItem.entityId,
                    uuid:
                      ecomStoreCartProduct.ecomStoreProductVariant
                        .ecomVendorProductVariant.uuid,
                    count: debouncedQuantity
                  }
                ]
              }
        );
      }
    }
  }, [debouncedQuantity]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuantity(quantity);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [quantity]);

  return (
    <AppStack>
      <WsCheckoutCartProductCardAlert
        hasChanges={hasChanges}
        isBundleProduct={!!ecomStoreCartPackage}
        isUnavailable={isUnavailable}
        latestVersionEcomStoreCartProduct={latestVersionEcomStoreCartProduct}
        onCustomizeProduct={() => onCustomize(ecomStoreCartProduct)}
        onRemoveUnavailableProducts={onRemoveUnavailableProducts}
        outOfStock={outOfStock}
        quantityLeft={quantityLeft}
        requiresPrintImagesUpdate={requiresPrintImagesUpdate}
        requiresProductUpdate={requiresProductUpdate}
        submitting={submitting}
        tooManyInCart={tooManyInCart}
      />

      <AppFlexbox>
        <AppStack
          component={Link}
          onClick={onNavigate}
          style={{ width: 80, height: 80 }}
          to={productUrl}
        >
          <Image
            fit="contain"
            src={previewImage}
            style={{ height: '100%', width: '100%' }}
          />
        </AppStack>
        <AppStack style={{ flex: 1, gap: 0 }}>
          <AppFlexbox
            style={{ alignItems: 'start', justifyContent: 'space-between' }}
          >
            <AppStack style={{ flex: 1, gap: 0 }}>
              <AppText
                component={Link}
                onClick={onNavigate}
                style={{
                  fontSize: 18,
                  fontWeight: 500,
                  color: '#000',
                  textDecoration: 'none'
                }}
                to={productUrl}
              >
                {lineItem.productName}
              </AppText>
              <ProductLineItemAddonsDisplay
                addOns={lineItem.addOns}
                showVariantOptionsOnSingleLine
              />

              <AppStack style={{ gap: 12, marginTop: 12 }}>
                <WsCheckoutCartProductCardBundleItems
                  imageSize={40}
                  packageItemsWithReferences={packageItemsWithReferences}
                />
              </AppStack>
            </AppStack>
            {removing ? (
              <Loader color="dark" size="18" />
            ) : (
              <ActionIcon
                color="#c40000"
                disabled={disabled}
                onClick={() => {
                  setRemoving(true);
                  onRemove(
                    lineItem.isBundleProduct
                      ? { pkEcomStoreCartPackages: [lineItem.entityId] }
                      : { pkEcomStoreCartProducts: [lineItem.entityId] },
                    () => {
                      setRemoving(false);
                    },
                    () => {
                      setRemoving(false);
                    }
                  );
                }}
                size="sm"
                style={{ marginTop: 3 }}
                variant="subtle"
              >
                <X />
              </ActionIcon>
            )}
          </AppFlexbox>
          <AppFlexbox
            style={{
              flex: 1,
              alignItems: 'end',
              justifyContent: 'space-between',
              marginTop: 5
            }}
          >
            <AppFlexbox style={{ gap: 10, alignItems: 'center' }}>
              <NumberInput
                allowNegative={false}
                disabled={
                  outOfStock ||
                  !!latestVersionEcomStoreCartProduct ||
                  disabled ||
                  removing
                }
                min={1}
                onChange={(v) => {
                  let newQuantity =
                    quantityLeft !== null && v > quantityLeft
                      ? quantityLeft
                      : v;
                  if (newQuantity <= 0) {
                    newQuantity = 1;
                  }
                  setQuantity(newQuantity);
                }}
                size="xs"
                style={{ flex: 1, maxWidth: 80 }}
                value={quantity}
              />
              {!latestVersionEcomStoreCartProduct && lineItem.isCustomizable && (
                <Anchor
                  onClick={onCustomize}
                  style={{ fontSize: 14, color: 'dodgerblue' }}
                  underline="always"
                >
                  Customize
                </Anchor>
              )}
            </AppFlexbox>

            <AppText style={{ fontSize: 18, fontWeight: 500 }}>
              <NumberFormatter
                decimalScale={2}
                displayType="text"
                fixedDecimalScale
                prefix="$"
                thousandSeparator
                value={(lineItem.subtotalInCents * lineItem.quantity) / 100}
              />
            </AppText>
          </AppFlexbox>
        </AppStack>
      </AppFlexbox>
    </AppStack>
  );
};

CartDrawerLineItem.propTypes = {
  disabled: PropTypes.bool,
  ecomStoreCart: PropTypes.object,
  lineItem: PropTypes.object,
  lineItemInfo: PropTypes.object,
  onCustomize: PropTypes.func,
  onNavigate: PropTypes.func,
  onRemove: PropTypes.func,
  onRemoveUnavailableProducts: PropTypes.func,
  onUpdate: PropTypes.func,
  productUrl: PropTypes.string,
  submitting: PropTypes.bool
};

export default CartDrawerLineItem;
